import React, { useCallback, useEffect, useRef, useState } from 'react';
import './ImageCropper.css';

/* =========================================================
   Crop a photo to a square: drag to move, slider (or mouse wheel) to zoom.
   The round guide shows what the avatar circle will show.
   props:
     src       data URL or image URL
     onDone(file)   a square JPEG (600×600) ready to upload
     onCancel()
   ========================================================= */
const OUT = 600;

const ImageCropper = ({ src, onDone, onCancel, title = 'Crop the photo' }) => {
  const boxRef = useRef(null);
  const imgRef = useRef(null);
  const drag = useRef(null);
  const [box, setBox] = useState(320);
  const [nat, setNat] = useState(null); // { w, h }
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // viewport size (fits phones)
  useEffect(() => {
    const fit = () => setBox(Math.min(360, Math.floor(window.innerWidth * 0.78)));
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onCancel();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const base = nat ? Math.max(box / nat.w, box / nat.h) : 1; // "cover" scale
  const scale = base * zoom;

  // keep the photo covering the square
  const clamp = useCallback(
    (p, s = scale) => {
      if (!nat) return p;
      const w = nat.w * s;
      const h = nat.h * s;
      return { x: Math.min(0, Math.max(box - w, p.x)), y: Math.min(0, Math.max(box - h, p.y)) };
    },
    [nat, box, scale]
  );

  const onLoad = (e) => {
    const w = e.currentTarget.naturalWidth;
    const h = e.currentTarget.naturalHeight;
    setNat({ w, h });
    const s = Math.max(box / w, box / h);
    setPos({ x: (box - w * s) / 2, y: (box - h * s) / 2 }); // centered
    setZoom(1);
  };

  // zoom around the center of the square
  const setZoomKeep = (z) => {
    const nz = Math.min(4, Math.max(1, z));
    const ns = base * nz;
    const cx = (box / 2 - pos.x) / scale;
    const cy = (box / 2 - pos.y) / scale;
    setZoom(nz);
    setPos(clamp({ x: box / 2 - cx * ns, y: box / 2 - cy * ns }, ns));
  };

  const down = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drag.current = { sx: e.clientX, sy: e.clientY, x: pos.x, y: pos.y };
  };
  const move = (e) => {
    if (!drag.current) return;
    const d = drag.current;
    setPos(clamp({ x: d.x + e.clientX - d.sx, y: d.y + e.clientY - d.sy }));
  };
  const up = () => {
    drag.current = null;
  };
  const wheel = (e) => {
    e.preventDefault();
    setZoomKeep(zoom - e.deltaY * 0.0015);
  };
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    el.addEventListener('wheel', wheel, { passive: false });
    return () => el.removeEventListener('wheel', wheel);
  });

  const nudge = (dx, dy) => setPos((p) => clamp({ x: p.x + dx, y: p.y + dy }));

  const done = () => {
    if (!nat || !imgRef.current) return;
    setBusy(true);
    setError('');
    try {
      const canvas = document.createElement('canvas');
      canvas.width = OUT;
      canvas.height = OUT;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, OUT, OUT);
      const sx = -pos.x / scale;
      const sy = -pos.y / scale;
      const sw = box / scale;
      ctx.drawImage(imgRef.current, sx, sy, sw, sw, 0, 0, OUT, OUT);
      canvas.toBlob(
        (blob) => {
          setBusy(false);
          if (!blob) return setError('Couldn’t crop this photo. Choose the file from your computer instead.');
          onDone(new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' }));
          return undefined;
        },
        'image/jpeg',
        0.9
      );
    } catch {
      // a photo from another website can't be read back: pick the file instead
      setBusy(false);
      setError('Couldn’t crop this photo. Choose the file from your computer instead.');
    }
  };

  return (
    <div className="rmcrop-overlay" role="dialog" aria-modal="true" aria-label={title} onMouseDown={(e) => e.target === e.currentTarget && onCancel()}>
      <div className="rmcrop">
        <h3>{title}</h3>
        <p>Drag to move · zoom with the slider</p>

        <div
          ref={boxRef}
          className="rmcrop-box"
          style={{ width: box, height: box }}
          onPointerDown={down}
          onPointerMove={move}
          onPointerUp={up}
          onPointerCancel={up}
          tabIndex={0}
          aria-label="Photo area. Use the arrow keys to move."
          onKeyDown={(e) => {
            const k = { ArrowLeft: [10, 0], ArrowRight: [-10, 0], ArrowUp: [0, 10], ArrowDown: [0, -10] }[e.key];
            if (k) {
              e.preventDefault();
              nudge(...k);
            }
          }}
        >
          <img
            ref={imgRef}
            src={src}
            alt=""
            crossOrigin={/^data:|^blob:/.test(src) ? undefined : 'anonymous'}
            onLoad={onLoad}
            onError={() => setError('Couldn’t open this photo.')}
            draggable={false}
            style={{ width: nat ? nat.w * scale : 'auto', height: nat ? nat.h * scale : 'auto', transform: `translate(${pos.x}px, ${pos.y}px)` }}
          />
          <span className="rmcrop-ring" aria-hidden="true" />
        </div>

        <label className="rmcrop-zoom">
          <span>Zoom</span>
          <input type="range" min="1" max="4" step="0.01" value={zoom} onChange={(e) => setZoomKeep(Number(e.target.value))} />
        </label>

        {error && <p className="rmcrop-error">{error}</p>}

        <div className="rmcrop-actions">
          <button type="button" className="rmcrop-btn rmcrop-btn--ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="rmcrop-btn" onClick={done} disabled={busy || !nat}>
            {busy ? 'Cropping…' : 'Use this photo'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageCropper;
